import React, { useRef, useState } from 'react';
import { X, UploadCloud, FileText, Trash2, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import patientCallingService, { PatientCallAttachment } from '../../../services/patientCalling.service';
import styles from './AttachmentUploadModal.module.css';

interface Props {
    serviceLogId: string;
    onClose: () => void;
    onUploaded: (attachments: PatientCallAttachment[]) => void;
}

type UploadStatus = 'pending' | 'uploading' | 'done' | 'error';

interface UploadItem {
    id: string;
    file: File;
    status: UploadStatus;
    progress: number;
    error?: string;
}

const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const AttachmentUploadModal: React.FC<Props> = ({ serviceLogId, onClose, onUploaded }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [items, setItems] = useState<UploadItem[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const addFiles = (files: FileList | File[]) => {
        const fresh: UploadItem[] = Array.from(files).map(f => ({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            file: f,
            status: 'pending',
            progress: 0,
        }));
        setItems(prev => [...prev, ...fresh]);
    };

    const removeItem = (id: string) => {
        setItems(prev => prev.filter(i => i.id !== id));
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (isUploading) return;
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addFiles(e.dataTransfer.files);
        }
    };

    const handleUpload = async () => {
        const queue = items.filter(i => i.status === 'pending' || i.status === 'error');
        if (queue.length === 0) return;
        setIsUploading(true);

        const uploaded: PatientCallAttachment[] = [];
        for (const item of queue) {
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'uploading', progress: 0, error: undefined } : i));
            try {
                const result = await patientCallingService.uploadAttachmentWithProgress(
                    serviceLogId,
                    item.file,
                    (percent) => {
                        setItems(prev => prev.map(i => i.id === item.id ? { ...i, progress: percent } : i));
                    },
                );
                uploaded.push(result);
                setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'done', progress: 100 } : i));
            } catch (err: any) {
                setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'error', error: err?.message || 'Upload failed' } : i));
            }
        }

        setIsUploading(false);
        if (uploaded.length > 0) onUploaded(uploaded);
    };

    const pendingCount = items.filter(i => i.status === 'pending' || i.status === 'error').length;
    const doneCount = items.filter(i => i.status === 'done').length;
    const hasWork = pendingCount > 0;

    const statusLabel = (item: UploadItem): string => {
        switch (item.status) {
            case 'pending': return 'Ready';
            case 'uploading': return `${item.progress}%`;
            case 'done': return 'Uploaded';
            case 'error': return 'Failed';
        }
    };

    return (
        <div className={styles.overlay} onClick={!isUploading ? onClose : undefined}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <div className={styles.header}>
                    <h3 className={styles.title}>
                        <UploadCloud size={17} />
                        Upload Attachments
                    </h3>
                    <button className={styles.closeBtn} onClick={onClose} disabled={isUploading} title="Close">
                        <X size={18} />
                    </button>
                </div>

                <div className={styles.body}>
                    <div
                        className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ''}`}
                        onClick={() => !isUploading && fileInputRef.current?.click()}
                        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                    >
                        <div className={styles.dropZoneIcon}>
                            <UploadCloud size={26} />
                        </div>
                        <p className={styles.dropZoneTitle}>Drop files here or click to browse</p>
                        <p className={styles.dropZoneHint}>Select multiple files — each will upload in sequence</p>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        onChange={e => {
                            if (e.target.files && e.target.files.length > 0) addFiles(e.target.files);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                    />

                    {items.length > 0 && (
                        <div className={styles.fileList}>
                            {items.map(item => {
                                const isRunning = item.status === 'uploading';
                                const cardClass = item.status === 'done'
                                    ? styles.fileItemDone
                                    : item.status === 'error'
                                        ? styles.fileItemError
                                        : '';
                                const statusClass = item.status === 'uploading' ? styles.statusUploading
                                    : item.status === 'done' ? styles.statusDone
                                    : item.status === 'error' ? styles.statusError
                                    : styles.statusPending;
                                const barClass = item.status === 'done'
                                    ? styles.progressBarDone
                                    : item.status === 'error'
                                        ? styles.progressBarError
                                        : '';
                                return (
                                    <div key={item.id} className={`${styles.fileItem} ${cardClass}`}>
                                        <div className={styles.fileItemTop}>
                                            <div className={styles.fileIcon}>
                                                {item.status === 'done' ? <CheckCircle size={16} />
                                                    : item.status === 'error' ? <AlertCircle size={16} />
                                                    : isRunning ? <Loader2 size={16} className={styles.spinning} />
                                                    : <FileText size={16} />}
                                            </div>
                                            <div className={styles.fileMeta}>
                                                <span className={styles.fileName} title={item.file.name}>{item.file.name}</span>
                                                <span className={styles.fileSub}>
                                                    <span>{formatSize(item.file.size)}</span>
                                                    <span>·</span>
                                                    <span className={`${styles.statusText} ${statusClass}`}>{statusLabel(item)}</span>
                                                </span>
                                            </div>
                                            {item.status !== 'done' && (
                                                <button
                                                    className={styles.removeBtn}
                                                    onClick={() => removeItem(item.id)}
                                                    disabled={isRunning}
                                                    title="Remove"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                        {(item.status === 'uploading' || item.status === 'done' || item.status === 'error') && (
                                            <div className={styles.progressWrap}>
                                                <div
                                                    className={`${styles.progressBar} ${barClass}`}
                                                    style={{ width: `${item.status === 'done' ? 100 : item.status === 'error' ? 100 : item.progress}%` }}
                                                />
                                            </div>
                                        )}
                                        {item.status === 'error' && item.error && (
                                            <div className={styles.errorMessage}>{item.error}</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className={styles.footer}>
                    <span className={styles.footerCount}>
                        {items.length === 0
                            ? 'No files selected'
                            : `${items.length} file${items.length === 1 ? '' : 's'} · ${doneCount} done${pendingCount > 0 ? ` · ${pendingCount} pending` : ''}`}
                    </span>
                    <div className={styles.footerRight}>
                        <button className={styles.outlineBtn} onClick={onClose} disabled={isUploading}>
                            {doneCount > 0 && pendingCount === 0 ? 'Close' : 'Cancel'}
                        </button>
                        <button className={styles.primaryBtn} onClick={handleUpload} disabled={isUploading || !hasWork}>
                            {isUploading ? (
                                <><Loader2 size={14} className={styles.spinning} /> Uploading...</>
                            ) : (
                                <><UploadCloud size={14} /> Upload{hasWork ? ` (${pendingCount})` : ''}</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AttachmentUploadModal;
